import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../cx';
import { formatNumber, type DsLocale } from '../format';
import { useCountUp } from '../hooks/useCountUp';

/** Scroll position (px) past which the index strip collapses. */
export const STRIP_COLLAPSE_AT = 64;
/** It only opens again this far above the collapse point, so it does not flicker at the threshold. */
export const STRIP_HYSTERESIS = 24;

/** Next collapsed state for a scroll position, with hysteresis. Pure, exported for tests. */
export function nextStripCollapsed(collapsed: boolean, scrollY: number): boolean {
  if (!collapsed && scrollY > STRIP_COLLAPSE_AT) return true;
  if (collapsed && scrollY < STRIP_COLLAPSE_AT - STRIP_HYSTERESIS) return false;
  return collapsed;
}

export type NavbarProps = {
  /** The logo link home. */
  logo: ReactNode;
  /** The SearchField, in the row from md; under the row on phones when `searchOnPhones`. */
  search?: ReactNode;
  searchOnPhones?: boolean;
  /** Account, menu, language. */
  actions?: ReactNode;
  /**
   * The live index strip (real counts from the daily stats snapshot, never typed). Shown from
   * 1024px and collapses after 64px of scroll.
   */
  strip?: ReactNode;
  /** Accessible name of the primary navigation landmark. */
  label: string;
  /** Sticky at the top (the real header); off in the showcase. */
  sticky?: boolean;
  className?: string;
};

/** The web header shell: logo, search and account in a 64px row, plus the collapsing live index strip. */
export function Navbar({
  logo,
  search,
  searchOnPhones = false,
  actions,
  strip,
  label,
  sticky = true,
  className,
}: NavbarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const frame = useRef(0);
  useEffect(() => {
    if (!strip || !sticky) return undefined;
    const onScroll = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => setCollapsed((c) => nextStripCollapsed(c, window.scrollY)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', onScroll);
    };
  }, [strip, sticky]);

  return (
    <header
      className={cx('border-b border-jc-border bg-jc-surface', sticky && 'sticky top-0 z-(--jc-z-sticky)', className)}
    >
      <nav aria-label={label} className="flex h-16 items-center gap-3 px-4 md:px-8 xl:px-12">
        <div className="flex shrink-0 items-center">{logo}</div>
        {search ? (
          <div className="hidden min-w-0 flex-1 justify-center md:flex">{search}</div>
        ) : (
          <div className="flex-1" />
        )}
        {actions ? <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div> : null}
      </nav>
      {search && searchOnPhones ? <div className="px-4 pb-3 md:hidden">{search}</div> : null}
      {strip ? (
        <div
          data-collapsed={collapsed || undefined}
          aria-hidden={collapsed || undefined}
          className={cx(
            'hidden overflow-hidden border-t border-jc-border bg-jc-page transition-all duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none lg:block',
            collapsed ? 'max-h-0 border-t-0 opacity-0' : 'max-h-8.5 opacity-100',
          )}
        >
          <div className="flex h-8.5 items-center gap-6 overflow-x-auto px-4 text-xs whitespace-nowrap text-jc-ink-secondary md:px-8 xl:px-12">
            {strip}
          </div>
        </div>
      ) : null}
    </header>
  );
}

export type StripStatProps = {
  /** The figure from the daily stats snapshot, never typed. */
  value: number;
  /** Page language, for grouping. */
  locale: DsLocale;
  label: ReactNode;
  href?: string;
  /** Counts up once per session; figures in one strip share this key. Omit for no count-up. */
  sessionKey?: string;
  /** Stagger between figures in the strip (e.g. index * 80). */
  delayMs?: number;
};

/** One figure in the index strip ("4,177 jobs"), locale-formatted, counting up once per session. */
export function StripStat({ value, locale, label, href, sessionKey, delayMs }: StripStatProps) {
  const shown = useCountUp(value, { sessionKey, delayMs });
  const body = (
    <>
      <span className="font-display font-bold tabular-nums text-jc-ink">{formatNumber(Math.round(shown), locale)}</span>{' '}
      {label}
    </>
  );
  return href ? (
    <a
      href={href}
      className="rounded-jc-info hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
    >
      {body}
    </a>
  ) : (
    <span>{body}</span>
  );
}
